import * as React from 'react';
import cn from 'classnames';
import { useTheme } from '../../theme';

interface HoverZoomPanImageProps {
	className?: string;
	src: string;
	alt?: string;
	zoom?: number;
	zoomTrigger?: 'hover' | 'click';
	/** Изменение направления движения мыши при зуме
	 * Выставляется если картике задан transform: rotate():
	 *
	 * rotate(0)/rotate(360) - normal
	 * rotate(90deg) - rotate-90
	 * rotate(-90deg) - rotate-270
	 * rotate(180deg)/rotate(-180deg) - rotate-270
	 */
	direction?: 'normal' | 'rotate-90' | 'rotate-180' | 'rotate-270';
}

const ImageZoom: React.FC<HoverZoomPanImageProps> = ({
	className,
	src,
	alt = 'zoom',
	zoom: propsZoom = 2,
	zoomTrigger = 'click',
	direction = 'normal'
}) => {
	const { ImageZoom: css } = useTheme('ImageZoom');

	const containerRef = React.useRef<HTMLDivElement>(null);
	const [transformOrigin, setTransformOrigin] = React.useState('center center');
	const [isZoomed, setIsZoomed] = React.useState(false);

	const zoom = Math.max(1, propsZoom);

	React.useEffect(() => {
		const container = containerRef.current;
		if (!container) {
			return undefined;
		}

		const onTouchMove = (e: TouchEvent) => {
			if (e.touches.length > 0 && (zoomTrigger === 'hover' || (zoomTrigger === 'click' && isZoomed))) {
				/** При zoom картинки на мобильной версии, блокируем скролл */
				e.preventDefault();
				handleMove(e.touches[0].clientX, e.touches[0].clientY);
			}
		};

		container.addEventListener('touchmove', onTouchMove, { passive: false });

		return () => {
			container.removeEventListener('touchmove', onTouchMove);
		};
	}, [zoomTrigger, isZoomed, direction]);

	const handleSetIsZoomed = (isZoomed: boolean) => {
		if (zoomTrigger === 'hover') {
			setIsZoomed(isZoomed);
		}
	};

	const handleMove = (clientX: number, clientY: number) => {
		const rect = containerRef.current?.getBoundingClientRect();

		if (!rect) {
			return;
		}

		let x = ((clientX - rect.left) / rect.width) * 100;
		let y = ((clientY - rect.top) / rect.height) * 100;

		switch (direction) {
			case 'rotate-90':
				[x, y] = [y, 100 - x];
				break;
			case 'rotate-180':
				[x, y] = [100 - x, 100 - y];
				break;
			case 'rotate-270':
				[x, y] = [100 - y, x];
				break;
			case 'normal':
			default:
				break;
		}

		setTransformOrigin(`${x}% ${y}%`);
	};

	const handleMouseMove = (e: React.MouseEvent) => {
		if (zoomTrigger === 'hover' || (zoomTrigger === 'click' && isZoomed)) {
			handleMove(e.clientX, e.clientY);
		}
	};

	const handleClick = (e: React.MouseEvent) => {
		if (zoomTrigger === 'click') {
			setIsZoomed(prev => {
				if (!prev) {
					handleMove(e.clientX, e.clientY);
				}

				return !prev;
			});
		}
	};

	return (
		<div
			ref={containerRef}
			className={cn(className, css.container)}
			onClick={handleClick}
			onMouseMove={handleMouseMove}
			onMouseEnter={() => handleSetIsZoomed(true)}
			onMouseLeave={() => handleSetIsZoomed(false)}
			onTouchStart={() => handleSetIsZoomed(true)}
			onTouchEnd={() => handleSetIsZoomed(false)}
			onTouchCancel={() => handleSetIsZoomed(false)}
		>
			<img
				src={src}
				alt={alt}
				className={css.image}
				style={{
					transform: isZoomed ? `scale(${zoom})` : 'scale(1)',
					transformOrigin
				}}
			/>
		</div>
	);
};

export default ImageZoom;
